import React from 'react';

import { ConsentPreferredTime, ConsentPreferredTimeLabels } from '../../../../@types/ConsentPreferredTime';
import type { Owner } from '../../../../@types/Owner';
import { OWNER_STEPS_LABELS } from '../../../../@types/Owner';
import Popup from '../../../common/Popup/Popup.module';

interface AgendaPopupProps {
	isOpen: boolean;
	onClose: () => void;
	titleSuffix: string;
	agendaLoading: boolean;
	agendaData: { waitingCurrent: Owner[]; waitingNext: Owner[]; toContact: Owner[] } | null;
	getOwnerFullName: (o: Owner) => string;
	getOwnerPhone: (o: Owner) => string;
	getOwnerMeetingAt: (o: Owner) => string;
}

const getPreferredTimeLabel = (preferredTime: string | null | undefined): string => {
	if (preferredTime === ConsentPreferredTime.MORNING || preferredTime === ConsentPreferredTime.EVENING || preferredTime === ConsentPreferredTime.NO_PREFERENCE) {
		return ConsentPreferredTimeLabels[preferredTime];
	}

	return ConsentPreferredTimeLabels[ConsentPreferredTime.NO_PREFERENCE];
};

function AgendaPopup({ isOpen, onClose, titleSuffix, agendaLoading, agendaData, getOwnerFullName, getOwnerPhone, getOwnerMeetingAt }: AgendaPopupProps) {
	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			title={<div className="flex items-center gap-2"><span role="img" aria-label="calendar">📅</span> Agenda du collaborateur{titleSuffix}</div>}
			additionalClass="w-full max-w-4xl"
		>
			{agendaLoading ? (
				<div className="p-4">Chargement…</div>
			) : (
				<div className="space-y-4 p-2 text-sm">
					{!agendaData && (
						<div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-700">Aucune donnée disponible.</div>
					)}
					{agendaData && (
						<>
							<div className="flex flex-wrap gap-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-800">
								<div>En attente (semaine en cours): <strong>{agendaData.waitingCurrent.length}</strong></div>
								<div>En attente (semaine prochaine): <strong>{agendaData.waitingNext.length}</strong></div>
								<div>À contacter: <strong>{agendaData.toContact.length}</strong></div>
							</div>

							<section>
								<div className="mb-2 font-semibold text-slate-800">En attente de RDV — Semaine en cours (<span className="text-rgpd-blue">{agendaData.waitingCurrent.length}</span>)</div>
								{agendaData.waitingCurrent.length === 0 ? (
									<div className="text-slate-500">Aucun rendez-vous prévu pour cette semaine.</div>
								) : (
									<div className="grid gap-3 sm:grid-cols-2">
										{agendaData.waitingCurrent.map((o: Owner) => (
											<div key={`wc-${o.id}`} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
												<div className="font-semibold text-emerald-900">{getOwnerFullName(o)}</div>
												<div className="text-slate-700">{o.address ?? ''}</div>
												{getOwnerPhone(o) && (
													<div className="text-slate-700"><span role="img" aria-label="phone">📞</span> {getOwnerPhone(o)}</div>
												)}
												<div className="text-slate-700">Prochain RDV: <strong>{getOwnerMeetingAt(o) || '—'}</strong></div>
												<div className="mt-1 text-xs text-slate-600">{OWNER_STEPS_LABELS?.[o.step] ?? ''}</div>
											</div>
										))}
									</div>
								)}
							</section>
							<section>
								<div className="mb-2 font-semibold text-slate-800">En attente de RDV — Semaine prochaine (<span className="text-rgpd-blue">{agendaData.waitingNext.length}</span>)</div>
								{agendaData.waitingNext.length === 0 ? (
									<div className="text-slate-500">Aucun rendez-vous prévu pour la semaine prochaine.</div>
								) : (
									<div className="grid gap-3 sm:grid-cols-2">
										{agendaData.waitingNext.map((o: Owner) => (
											<div key={`wn-${o.id}`} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
												<div className="font-semibold text-amber-900">{getOwnerFullName(o)}</div>
												<div className="text-slate-700">{o.address ?? ''}</div>
												{getOwnerPhone(o) && (
													<div className="text-slate-700"><span role="img" aria-label="phone">📞</span> {getOwnerPhone(o)}</div>
												)}
												<div className="text-slate-700">Prochain RDV: <strong>{getOwnerMeetingAt(o) || '—'}</strong></div>
												<div className="mt-1 text-xs text-slate-600">{OWNER_STEPS_LABELS?.[o.step] ?? ''}</div>
											</div>
										))}
									</div>
								)}
							</section>
							<section>
								<div className="mb-2 font-semibold text-slate-800">À contacter (<span className="text-rgpd-blue">{agendaData.toContact.length}</span>)</div>
								{agendaData.toContact.length === 0 ? (
									<div className="text-slate-500">Aucun prise de contact prévue.</div>
								) : (
									<div className="grid gap-3 sm:grid-cols-2">
										{agendaData.toContact.map((o: Owner) => (
											<div key={`tc-${o.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
												<div className="font-semibold text-slate-900">{getOwnerFullName(o)}</div>
												<div className="text-slate-700">{o.address ?? ''}</div>
												{getOwnerPhone(o) && (
													<div className="text-slate-700"><span role="img" aria-label="phone">📞</span> {getOwnerPhone(o)}</div>
												)}
												{Array.isArray(o.consents) && o.consents.length > 0 && o.consents[0]?.recallAt && (
													<div className="text-slate-700">
														Rappel souhaité: <strong>{new Date(o.consents[0].recallAt).toLocaleDateString('fr-FR')}</strong>
														{o.consents[0].preferredTime && (
															<span> — {getPreferredTimeLabel(o.consents[0].preferredTime)}</span>
														)}
													</div>
												)}
												<div className="mt-1 text-xs text-slate-600">{OWNER_STEPS_LABELS?.[o.step] ?? ''}</div>
											</div>
										))}
									</div>
								)}
							</section>
						</>
					)}
				</div>
			)}
		</Popup>
	);
}

export default AgendaPopup;


